"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { Search, Loader2, Package, Truck, CheckCircle2, Clock, XCircle, ExternalLink, Copy } from "lucide-react";
import { toast } from "sonner";

const STATUS_MAP: any = {
  pending: { label: "Bekliyor", icon: Clock, color: "text-yellow-500", bg: "bg-yellow-500/10" },
  awaiting_payment: { label: "Ödeme Bekliyor", icon: Clock, color: "text-amber-500", bg: "bg-amber-500/10" },
  payment_confirmed: { label: "Ödeme Onaylandı", icon: CheckCircle2, color: "text-blue-500", bg: "bg-blue-500/10" },
  confirmed: { label: "Onaylandı", icon: CheckCircle2, color: "text-blue-500", bg: "bg-blue-500/10" },
  shipped: { label: "Kargoda", icon: Truck, color: "text-purple-500", bg: "bg-purple-500/10" },
  delivered: { label: "Teslim Edildi", icon: CheckCircle2, color: "text-green-500", bg: "bg-green-500/10" },
  cancelled: { label: "İptal Edildi", icon: XCircle, color: "text-red-500", bg: "bg-red-500/10" },
};

const CARGO_LINKS: Record<string, string> = {
  aras: "https://kargotakip.araskargo.com.tr/mainpage.aspx?code=",
  yurtici: "https://www.yurticikargo.com/tr/online-servisler/gonderi-sorgula?code=",
  dhl: "https://www.dhl.com/tr-tr/home/tracking/tracking-express.html?submit=1&tracking-id=",
  ptt: "https://gonderitakip.ptt.gov.tr/Track/Verify?q=",
};

export default function OrderTrackingClient() {
  const { data: session } = useSession();
  const [orderId, setOrderId] = useState("");
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState<any>(null);
  const [error, setError] = useState("");

  // Giriş yapmış kullanıcının siparişleri — sayfaya girer girmez otomatik gösterilir,
  // sipariş numarası elle aranmasına gerek kalmaz.
  const [myOrders, setMyOrders] = useState<any[]>([]);
  const [myOrdersLoading, setMyOrdersLoading] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [manualSearchOpen, setManualSearchOpen] = useState(false);

  useEffect(() => {
    if (!session?.user) return;
    setMyOrdersLoading(true);
    fetch("/api/user/orders")
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setMyOrders(data);
          setOrder(data[0]);
          setSelectedOrderId(data[0].id);
        }
      })
      .catch(() => {})
      .finally(() => setMyOrdersLoading(false));
  }, [session?.user]);

  const handleTrack = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId) return;

    setLoading(true);
    setError("");
    setOrder(null);
    setSelectedOrderId(null);

    try {
      const cleanId = orderId.replace("#", "").trim();
      const res = await fetch(`/api/orders/${cleanId}`);
      const data = await res.json();

      if (res.ok) {
        setOrder(data);
      } else {
        setError(data.error || "Sipariş bulunamadı.");
      }
    } catch (err) {
      setError("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-12">
      {session?.user && myOrdersLoading && (
        <div className="flex items-center gap-2 text-[11px] text-[#7A7670] uppercase tracking-widest">
          <Loader2 size={13} className="animate-spin text-[#C9A84C]" /> Siparişleriniz yükleniyor...
        </div>
      )}

      {session?.user && myOrders.length > 0 && (
        <div className="space-y-3">
          <p className="text-[10px] text-[#7A7670] uppercase tracking-widest">Siparişleriniz — Durumu Görmek İçin Seçin</p>
          <div className="flex flex-wrap gap-2">
            {myOrders.map((o) => {
              const isActive = selectedOrderId === o.id;
              const Icon = STATUS_MAP[o.status]?.icon || Clock;
              return (
                <button
                  key={o.id}
                  type="button"
                  onClick={() => { setOrder(o); setSelectedOrderId(o.id); setError(""); setOrderId(""); }}
                  className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-xs transition-all ${
                    isActive
                      ? "border-[#C9A84C] bg-[#C9A84C]/8 text-[#F4F0E8]"
                      : "border-[rgba(201,168,76,0.12)] bg-black/10 text-[#7A7670] hover:border-[rgba(201,168,76,0.3)]"
                  }`}
                >
                  <Icon size={13} className={STATUS_MAP[o.status]?.color} />
                  <span className="font-mono">#{o.id.slice(-8).toUpperCase()}</span>
                  <span className={STATUS_MAP[o.status]?.color}>{STATUS_MAP[o.status]?.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Manuel sipariş no ile arama — misafirler için her zaman açık, üyeler için
          "farklı bir sipariş" aramak isteyenlere özel, gereksiz kalabalık yapmasın diye gizli başlar */}
      {(!session?.user || manualSearchOpen) ? (
      <form onSubmit={handleTrack} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7A7670]" size={20} />
          <input
            type="text"
            required
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="Sipariş No (Örn: #AB12CD34)"
            className="w-full bg-[#111114] border border-[rgba(201,168,76,0.12)] text-[#F4F0E8] pl-12 pr-4 py-4 rounded focus:outline-none focus:border-[#C9A84C] transition"
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="bg-[#C9A84C] text-black px-8 py-4 rounded font-bold uppercase text-xs tracking-widest hover:bg-[#F4F0E8] transition-all disabled:opacity-50"
        >
          {loading ? <Loader2 className="animate-spin" size={20} /> : "SORGULA"}
        </button>
      </form>
      ) : (
        <button
          type="button"
          onClick={() => setManualSearchOpen(true)}
          className="text-[11px] text-[#7A7670] hover:text-[#C9A84C] underline underline-offset-2 uppercase tracking-widest transition"
        >
          Farklı Bir Sipariş Numarasıyla Arama Yap
        </button>
      )}

      {error && (
        <div className="p-4 bg-red-500/10 border border-red-500/20 text-red-500 rounded text-sm">
          {error}
        </div>
      )}

      {order && (
        <div className="bg-[#111114] border border-[#C9A84C]/20 rounded-lg p-8 space-y-8 text-left animate-in fade-in slide-in-from-bottom-4 duration-500">
          <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[rgba(201,168,76,0.12)] pb-6">
            <div className="space-y-1">
              <p className="text-[10px] text-[#7A7670] uppercase tracking-widest font-bold">Sipariş Durumu</p>
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-full ${STATUS_MAP[order.status]?.bg}`}>
                  {(() => {
                    const Icon = STATUS_MAP[order.status]?.icon || Clock;
                    return <Icon size={20} className={STATUS_MAP[order.status]?.color} />;
                  })()}
                </div>
                <span className={`text-2xl font-serif ${STATUS_MAP[order.status]?.color}`}>
                  {STATUS_MAP[order.status]?.label}
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-[#7A7670] uppercase tracking-widest font-bold">Sipariş No</p>
              <p className="text-[#F4F0E8] font-mono text-xl uppercase">#{order.id.slice(-8)}</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="space-y-4">
              <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.2em] font-bold">Müşteri Bilgileri</p>
              <div className="space-y-1">
                <p className="text-[#F4F0E8] font-medium">{order.guestEmail}</p>
                <p className="text-sm text-[#7A7670]">{new Date(order.createdAt).toLocaleString('tr-TR')}</p>
              </div>
            </div>
            {order.trackingNumber && order.trackingConfirmed && (
              <div className="space-y-4 bg-black/20 border border-[rgba(201,168,76,0.1)] rounded-xl p-6">
                <div>
                  <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.2em] font-bold mb-2">Kargo Takip Bilgileri</p>
                  <p className="text-[11px] text-[#7A7670] uppercase tracking-widest mb-1">
                    {order.trackingCompany === "aras" && "Aras Kargo - "}
                    {order.trackingCompany === "yurtici" && "Yurtiçi Kargo - "}
                    {order.trackingCompany === "dhl" && "DHL Express - "}
                    {order.trackingCompany === "ptt" && "PTT Kargo - "}
                    Takip Numarası
                  </p>
                  <div className="flex items-center gap-4">
                    <p className="text-[#F4F0E8] font-mono text-xl">{order.trackingNumber}</p>
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(order.trackingNumber);
                        toast.success("Kopyalandı!");
                      }}
                      className="p-2 rounded bg-[#C9A84C]/10 text-[#C9A84C] hover:bg-[#C9A84C] hover:text-black transition"
                      title="Kopyala"
                    >
                      <Copy size={14} />
                    </button>
                  </div>
                </div>

                {order.trackingCompany && CARGO_LINKS[order.trackingCompany] && (
                  <a
                    href={`${CARGO_LINKS[order.trackingCompany]}${order.trackingNumber}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 py-3 px-6 rounded bg-[#C9A84C] text-black font-bold uppercase tracking-widest text-[10px] hover:bg-[#D4B664] transition"
                  >
                    <ExternalLink size={14} /> Kargo Takip Sayfasına Git
                  </a>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4 pt-4 border-t border-[rgba(201,168,76,0.08)]">
            <p className="text-[10px] text-[#C9A84C] uppercase tracking-[0.2em] font-bold">Ürünler</p>
            <div className="space-y-3">
              {order.items.map((item: any) => (
                <div key={item.id} className="flex justify-between items-center text-sm">
                  <div className="flex items-center gap-3">
                    <span className="text-[#7A7670]">{item.quantity}x</span>
                    <span className="text-[#F4F0E8]">{item.product.nameTR}</span>
                  </div>
                  <span className="text-[#7A7670]">₺{item.price.toLocaleString('tr-TR')}</span>
                </div>
              ))}
              <div className="pt-3 border-t border-[rgba(201,168,76,0.05)] flex justify-between items-end">
                <span className="text-[#7A7670] text-xs">Toplam Tutar</span>
                <span className="text-2xl text-[#C9A84C] font-serif font-light">₺{order.total.toLocaleString('tr-TR')}</span>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
